import { useState } from 'react';
import { engagementsApi } from '../../api/engagements.js';
import { buildMailtoLink } from '../../utils/mailto.js';
import { useSender } from '../../hooks/useSender.js';
import { ErrorAlert } from './ErrorAlert.jsx';

/**
 * Generate / copy / send / revoke a speaker portal link for one engagement.
 * Shared by EngagementDetailPage's Portal tab and SpeakerDetailPage's
 * onboarding section — the underlying engagement may or may not have an
 * event assigned yet (onboarding can start before a specific event is picked).
 */
export function PortalInviteControls({ engagement, event, speaker, onUpdate }) {
  const sender = useSender();
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [portalUrl, setPortalUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { portalUrl: url } = await engagementsApi.createInvitation(engagement.id);
      setPortalUrl(url);
      const updated = await engagementsApi.get(engagement.id);
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async () => {
    if (!confirm('Revoke this invitation? The speaker will no longer be able to access their portal.')) return;
    setRevoking(true);
    try {
      await engagementsApi.revokeInvitation(engagement.id);
      setPortalUrl(null);
      const updated = await engagementsApi.get(engagement.id);
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setRevoking(false);
    }
  };

  const copy = async () => {
    const url = portalUrl ?? (engagement.activeTokenId ? `${window.location.origin}/portal/${engagement.activeTokenId}` : null);
    if (!url) return;
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentUrl = portalUrl ?? (engagement.activeTokenId
    ? `${window.location.origin}/portal/${engagement.activeTokenId}`
    : null);

  return (
    <div className="stack">
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <div className="card stack">
        <h3 style={{ fontSize: 'var(--text-base)' }}>Speaker portal link</h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          The portal link gives the speaker passwordless access to submit their biography, headshot, and signed agreement.
          Links are unique, time-limited (90 days), and can be revoked at any time.
        </p>

        {currentUrl ? (
          <>
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                readOnly
                value={currentUrl}
                className="form-input"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}
                onClick={(e) => e.target.select()}
                aria-label="Portal URL"
              />
              <button className="btn btn-secondary btn-sm" onClick={copy}>
                {copied ? '✓ Copied' : 'Copy link'}
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={!speaker?.email}
                onClick={() => { window.location.href = buildMailtoLink('Invitation', engagement, event, speaker, sender); }}
              >
                Send invite
              </button>
            </div>
            <div className="inline">
              <button className="btn btn-primary btn-sm" onClick={generate} disabled={generating}>
                {generating ? 'Generating…' : 'Reissue (invalidates current link)'}
              </button>
              <button className="btn btn-danger btn-sm" onClick={revoke} disabled={revoking}>
                {revoking ? 'Revoking…' : 'Revoke access'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 'var(--text-sm)' }}>No active invitation link.</p>
            <button className="btn btn-primary" onClick={generate} disabled={generating} style={{ alignSelf: 'flex-start' }}>
              {generating ? 'Generating…' : 'Generate invitation link'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
